import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { API_BASE_URL, authenticatedFetch } from '../utils/api';
import SessionListItem from './SessionListItem';
import './ProjectsNavMenu.css';

const ProjectsNavMenu = () => {
  const [projects, setProjects] = useState([]);
  const [sessionsByProject, setSessionsByProject] = useState({}); // { projectId: [sessions] }
  const [expandedProjects, setExpandedProjects] = useState(new Set()); // Track which projects have expanded sessions
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Fetch all projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const resp = await authenticatedFetch(`${API_BASE_URL}/casc/v1/api/chat/project/list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page: 1, limit: 100 }), // Get up to 100 projects
        });
        const json = await resp.json().catch(() => ({}));
        if (resp.ok && json?.success && json?.data?.items) {
          setProjects(json.data.items);
          
          // Fetch sessions for each project
          const sessionsMap = {};
          const fetchPromises = json.data.items.map(async (project) => {
            try {
              const sessionResp = await authenticatedFetch(`${API_BASE_URL}/casc/v1/api/chat/session/list`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ projectId: project.project_id, page: 1, limit: 50 }), // Get up to 50 sessions per project
              });
              const sessionJson = await sessionResp.json().catch(() => ({}));
              if (sessionResp.ok && sessionJson?.success && sessionJson?.data?.items) {
                sessionsMap[project.project_id] = sessionJson.data.items;
              }
            } catch (err) {
              console.warn(`Failed to fetch sessions for project ${project.project_id}:`, err);
            }
          });
          await Promise.all(fetchPromises);
          setSessionsByProject(sessionsMap);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleSessionRenamed = useCallback((projectId, renamedSessionId, name) => {
    setSessionsByProject((prev) => {
      const sessions = prev[projectId];
      if (!sessions) return prev;
      return {
        ...prev,
        [projectId]: sessions.map((s) =>
          s.session_id === renamedSessionId ? { ...s, name } : s
        ),
      };
    });
  }, []);

  useEffect(() => {
    const onRenamed = (ev) => {
      const d = ev?.detail || {};
      if (!d.session_id || !d.name || !d.project_id) return;
      handleSessionRenamed(d.project_id, d.session_id, d.name);
    };
    window.addEventListener('cascade:session-renamed', onRenamed);
    return () => window.removeEventListener('cascade:session-renamed', onRenamed);
  }, [handleSessionRenamed]);

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const isProjectActive = (projectId) => {
    return location.pathname.startsWith(`/p/${projectId}`);
  };

  const isSessionActive = (projectId, sessionId) => {
    return location.pathname === `/p/${projectId}/c/${sessionId}`;
  };

  if (loading) {
    return (
      <div className="projects-nav-menu">
        <div className="projects-nav-loading">
          <i className="fas fa-spinner fa-pulse"></i> Loading projects...
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return null; // Don't show anything if no projects
  }

  return (
    <div className="projects-nav-menu">
      <div className="projects-nav-header">
        <i className="fas fa-folder-open"></i>
        <span>Projects</span>
      </div>
      <ul className="projects-nav-list">
        {projects.map((project) => {
          const sessions = sessionsByProject[project.project_id] || [];
          const isExpanded = expandedProjects.has(project.project_id);
          const isActive = isProjectActive(project.project_id);
          const hasSessions = sessions.length > 0;

          return (
            <li key={project.project_id} className={`project-nav-item ${isActive ? 'active' : ''}`}>
              <div className="project-nav-header">
                <NavLink
                  to={`/p/${project.project_id}`}
                  className="project-nav-link"
                  onClick={(e) => {
                    // If clicking the project link, don't toggle
                    e.stopPropagation();
                  }}
                >
                  <i className="fas fa-folder"></i>
                  <span className="project-nav-name" title={project.name}>
                    {project.name || 'Unnamed Project'}
                  </span>
                </NavLink>
                {hasSessions && (
                  <button
                    className="project-nav-toggle"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleProject(project.project_id);
                    }}
                    aria-label={isExpanded ? 'Collapse sessions' : 'Expand sessions'}
                  >
                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                  </button>
                )}
              </div>
              {hasSessions && isExpanded && (
                <ul className="sessions-nav-list">
                  {sessions.map((session) => (
                    <SessionListItem
                      key={session.session_id}
                      variant="nav"
                      session={session}
                      projectId={project.project_id}
                      isActive={isSessionActive(project.project_id, session.session_id)}
                      onRenamed={(renamedSessionId, name) =>
                        handleSessionRenamed(project.project_id, renamedSessionId, name)
                      }
                    />
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ProjectsNavMenu;
